import { useEffect, useId, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { ChevronDown, ChevronUp, User, UserCog, LogOut } from 'lucide-react';
import { useAuthStore } from '@auth/authStore';
import styles from './AccountMenu.module.css';

function AccountControl({ placement }) {
  const companyName = useAuthStore(state => state.companyName);
  const firstName = useAuthStore(state => state.userFirstName);
  const lastName = useAuthStore(state => state.userLastName);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const panelId = useId();
  const name = [firstName, lastName].filter(Boolean).join(' ') || 'Your account';
  const company = companyName || 'Account settings';
  const initials = [firstName, lastName]
    .map(value => String(value || '').trim().slice(0, 1)).join('').toUpperCase() || 'U';
  const Chevron = placement === 'sidebar' ? ChevronUp : ChevronDown;

  useEffect(() => {
    if (!open) return;
    function outside(event) {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);

  function close() {
    setOpen(false);
  }

  return (
    <div
      ref={containerRef}
      className={styles.account}
      data-placement={placement}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) close();
      }}
      onKeyDown={event => {
        if (event.key === 'Escape' && open) {
          event.preventDefault();
          event.stopPropagation();
          close();
          triggerRef.current?.focus();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label={'Account options for ' + name}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen(value => !value)}
      >
        <span className={styles.avatar} aria-hidden="true">{initials}</span>
        <span className={styles.identity}>
          <span className={styles.name} title={name}>{name}</span>
          <span className={styles.company} title={company}>{company}</span>
        </span>
        <Chevron className={styles.chevron} aria-hidden="true" />
      </button>

      {open && (
        <div id={panelId} className={styles.panel} role="group" aria-label="Account options">
          <p className={styles.caption}>ACCOUNT</p>
          <NavLink to="/settings" className={styles.action} onClick={close}>
            <User aria-hidden="true" /><span>Profile</span>
          </NavLink>
          <NavLink to="/settings" className={styles.action} onClick={close}>
            <UserCog aria-hidden="true" /><span>Options</span>
          </NavLink>
          <div className={styles.divider} />
          <NavLink
            to="/logout"
            className={styles.action}
            onClick={() => {
              close();
              useAuthStore.getState().logout();
            }}
          >
            <LogOut aria-hidden="true" /><span>Log out</span>
          </NavLink>
        </div>
      )}
    </div>
  );
}

export default function AccountMenu({ placement = 'topbar' }) {
  const location = useLocation();
  // Reset the disclosure when navigation changes, including Back/Forward.
  return <AccountControl key={location.key} placement={placement} />;
}